import { axiosInstance } from "@/lib/axiosInstance";

/**
 * Interviews live on the recruiter service (:8013) and are returned as plain
 * objects — there is NO { success, data } envelope. Do not copy the student
 * app's response handler here.
 *
 * Every write mirrors to the student app automatically, so the frontend must
 * never fire an extra call to keep the two sides in sync:
 *   POST                 → student sees the interview · application becomes
 *                          `interview_scheduled` · "Interview Scheduled" email
 *   PATCH scheduled_at   → student's copy updates in place · "Rescheduled" email
 *   PATCH status:cancelled → drops off the student's upcoming list · "Cancelled" email
 *   PATCH status:completed → application becomes `interviewed` (no email)
 *
 * `feedback` / `rating` stay inside the recruiter product — they are never
 * exposed to the student.
 */

export type InterviewTypeValue =
  | "phone"
  | "video"
  | "in_person"
  | "technical"
  | "hr";

export type InterviewStatusValue =
  | "scheduled"
  | "completed"
  | "cancelled"
  | "no_show";

export type InterviewCreateType = {
  interview_type: InterviewTypeValue;
  scheduled_at: string; // ISO-8601
  duration_minutes?: number; // API default is 30 (the DB default of 60 is unused)
  meeting_link?: string;
  venue?: string;
};

export type InterviewUpdateType = Partial<
  Omit<InterviewCreateType, "interview_type">
> & {
  status?: InterviewStatusValue;
  feedback?: string;
  rating?: number; // 1–5
};

export type InterviewType = {
  id: string;
  application_id: string;
  interview_type: InterviewTypeValue;
  scheduled_at: string;
  duration_minutes: number;
  meeting_link: string | null;
  venue: string | null;
  status: InterviewStatusValue;
  feedback: string | null;
  rating: number | null;
  created_at: string;
};

export const createInterview = async (
  applicationId: string,
  data: InterviewCreateType
): Promise<InterviewType> => {
  const response = await axiosInstance.post(
    `/v1/applications/${applicationId}/interviews`,
    data
  );
  return response.data;
};

/** Returns every round for the application, ordered by scheduled_at ASC. */
export const fetchApplicationInterviews = async (
  applicationId: string
): Promise<InterviewType[]> => {
  const response = await axiosInstance.get(
    `/v1/applications/${applicationId}/interviews`
  );
  return response.data;
};

/**
 * Rescheduling is a PATCH — never a second POST, which would create an extra
 * round and show the student two interviews.
 *
 * To clear `meeting_link` or `venue` send `""`, not `null`: the handler runs
 * `model_dump(exclude_none=True)`, so a null is dropped from the payload and
 * the old value survives. The sync boundary normalises `""` to null before it
 * reaches the student, so the student never sees a blank string.
 *
 * The "Rescheduled" email fires only when `scheduled_at` actually changes, so
 * sending the whole form on every edit is safe — changing only the link, venue
 * or duration sends nothing.
 */
export const updateInterview = async (
  interviewId: string,
  data: InterviewUpdateType
): Promise<InterviewType> => {
  const response = await axiosInstance.patch(
    `/v1/interviews/${interviewId}`,
    data
  );
  return response.data;
};
